import type { CampaignFormatId, CampaignPlatformId } from '../campaign/types';

export const AUTONOMY_CAPABILITIES = [
  'campaign_creation',
  'campaign_strategy',
  'text_generation',
  'media_generation',
  'scheduling',
  'schedule_rebalancing',
  'publishing',
  'campaign_adaptation',
  'resurfacing',
  'sensitive_topic_handling',
  'synthetic_media_handling',
  'low_confidence_output_handling',
] as const;

export type AutonomyCapability = (typeof AUTONOMY_CAPABILITIES)[number];
export type AutonomyPermission = 'manual_only' | 'approval_required' | 'autonomous_allowed';
export type AutonomyCapabilityRules = Record<AutonomyCapability, AutonomyPermission>;

export const SAFE_AUTONOMY_CAPABILITY_RULES: AutonomyCapabilityRules = Object.fromEntries(
  AUTONOMY_CAPABILITIES.map((capability) => [capability, 'manual_only']),
) as AutonomyCapabilityRules;

export interface AutonomyPolicy {
  id: string;
  publicationId: string;
  versionNumber: number;
  capabilityRules: AutonomyCapabilityRules;
  createdBy: string | null;
  origin: string;
  createdAt: string;
  updatedAt: string;
}

export const ADVANCED_AUTONOMY_CAPABILITIES = [
  'source_campaign_creation','campaign_intelligence_generation','social_output_generation',
  'social_output_regeneration','media_generation','safety_assisted_review','campaign_adaptation',
  'schedule_creation','schedule_rebalancing','resurfacing_assessment',
  'resurfacing_opportunity_acceptance','resurfacing_campaign_creation','factual_review_resolution',
  'content_approval','safety_override','queue_enqueue','social_publication','crisis_pause_resume',
  'multi_source_campaign_creation',
] as const;
export type AdvancedAutonomyCapability=(typeof ADVANCED_AUTONOMY_CAPABILITIES)[number];
export type AdvancedAutonomyLevel='prohibited'|'human_required'|'autonomous_allowed';
export type AutonomyDecisionValue='allowed'|'human_required'|'prohibited'|'held';
export interface AutonomyConditions {resourceType?:string;resourceId?:string;platform?:string;contentType?:string;campaignOrigin?:string;safetyState?:string;minimumConfidence?:'low'|'medium'|'high';currentnessState?:string;sourceSensitivity?:string;topic?:string;campaignPriority?:string;minimumPerformanceConfidence?:'low'|'medium'|'high';resurfacingStatus?:string;maximumAiCredits?:number;outputFormat?:string;destinationId?:string;startTimeUtc?:string;endTimeUtc?:string;humanLocked?:boolean;}
export interface AdvancedAutonomyRule {id:string;capability:AdvancedAutonomyCapability;level:AdvancedAutonomyLevel;priority:number;conditions:AutonomyConditions;effectiveFrom:string|null;expiresAt:string|null;}
export interface AdvancedAutonomyPolicy {id:string;publicationId:string;versionNumber:number;previousPolicyId:string|null;effectiveFrom:string;expiresAt:string|null;changeReason:string;createdBy:string;createdAt:string;rules:AdvancedAutonomyRule[];}
export interface AutonomyDecisionView {id:string;publicationId:string;policyId:string|null;policyVersion:number|null;capability:AdvancedAutonomyCapability;resourceType:string;resourceId:string;resourceVersionId:string|null;decision:AutonomyDecisionValue;reasonCode:string;reason:string;applicableRuleIds:string[];decidedAt:string;}

export interface DistributionAssessment {
  id: string;
  sourceVersionId: string;
  analysisBundleId: string;
  contentType: string;
  editorialPurpose: string;
  newsVelocity: string;
  initialUrgency: string;
  campaignHorizon: string;
  evergreenPotential: string;
  resurfacingPotential: string;
  factualAgeingRisk: string;
  platformFit: Record<string, unknown>;
  formatOpportunities: string[];
  angleCapacity: number;
  audienceSegments: string[];
  distributionPriority: string;
  confidence: Record<string, unknown>;
  reasoningSummary: string;
  createdAt: string;
}

export interface TemporalAssessment {
  id: string;
  sourceVersionId: string;
  analysisBundleId: string;
  initialUrgency: string;
  primaryRelevanceWindow: Record<string, unknown>;
  primaryCampaignHorizon: string;
  evergreenHorizon: string | null;
  factualReviewInterval: string | null;
  resurfacingEligible: boolean;
  resurfacingTriggers: string[];
  expiryConditions: string[];
  currentnessConfidence: Record<string, unknown>;
  createdAt: string;
}

export interface CampaignStrategyVersion {
  id: string;
  campaignId: string;
  versionNumber: number;
  sourceVersionId: string;
  analysisBundleId: string;
  objective: string;
  campaignHorizon: string;
  outputVolume: number;
  platformAllocation: Partial<Record<CampaignPlatformId, number>>;
  formatAllocation: Partial<Record<CampaignFormatId, number>>;
  cadence: Record<string, unknown>;
  priority: string;
  mediaIntent: Record<string, unknown>;
  resurfacingPosture: string;
  completionConditions: string[];
  reasoning: string;
  triggerType: string;
  origin: string;
  createdBy: string | null;
  createdAt: string;
}

export interface CampaignAngle {
  id: string;
  campaignId: string;
  campaignStrategyVersionId: string;
  label: string;
  editorialPurpose: string;
  grounding: Record<string, unknown>;
  priority: number;
  status: string;
  createdBy: string | null;
  createdAt: string;
}

export type SocialOutputLifecycleState = 'draft' | 'ready' | 'superseded' | 'archived';

export interface SocialOutput {
  id: string;
  campaignId: string;
  campaignAngleId: string;
  /** Existing Spox PublishingDestination is the durable platform-account identity. */
  platformAccountId: string;
  platform: CampaignPlatformId;
  format: CampaignFormatId;
  editorialPurpose: string;
  temporalIntent: Record<string, unknown>;
  lifecycleState: SocialOutputLifecycleState;
  legacyMasterPostId: string | null;
  legacyPlatformVariantId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SocialOutputVersion {
  id: string;
  socialOutputId: string;
  versionNumber: number;
  contentPayload: Record<string, unknown>;
  generationMetadata: Record<string, unknown>;
  provenance: Record<string, unknown>;
  origin: string;
  createdBy: string | null;
  legacyPlatformVariantVersionId: string | null;
  createdAt: string;
}

export type OrchestrationDecisionType = 'place' | 'move' | 'defer' | 'replace' | 'cancel' | 'hold';

export interface V03ScheduleItem {
  id: string;
  campaignId: string;
  socialOutputId: string;
  socialOutputVersionId: string;
  publishingDestinationId: string;
  scheduledAtUtc: string;
  timezone: string;
  status: 'scheduled' | 'held' | 'deferred' | 'displaced' | 'cancelled';
  locked: boolean;
  manual: boolean;
  reason: string;
}

export interface OrchestrationPlanView {
  id: string;
  publicationId: string;
  versionNumber: number;
  horizonStartUtc: string;
  horizonEndUtc: string;
  inputFingerprint: string;
  triggerType: 'initial' | 'replan' | 'manual';
  reasoningSummary: string;
  items: Array<V03ScheduleItem & { campaignTitle: string; sourceTitle: string | null; platform: string; format: string; decisionType: OrchestrationDecisionType; safetyStatus: string; executionStatus: string }>;
}
